import { useState, useEffect } from "react";
import { X, MapPin, CalendarBlank, Ticket, Globe, NavigationArrow, Clock, Users, Storefront, CurrencyDollar, Tag, ArrowSquareOut, CalendarPlus, Envelope, Phone, BookmarkSimple, Sun, Cloud, CloudRain, CloudLightning, Snowflake, CloudSun, Thermometer } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { format } from "date-fns";
import axios from "axios";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WeatherIcon = ({ icon, className = "w-6 h-6" }) => {
  const icons = {
    "sun": <Sun weight="duotone" className={`${className} text-amber-500`} />,
    "cloud-sun": <CloudSun weight="duotone" className={`${className} text-amber-400`} />,
    "cloud": <Cloud weight="duotone" className={`${className} text-gray-400`} />,
    "cloud-rain": <CloudRain weight="duotone" className={`${className} text-blue-400`} />,
    "cloud-lightning": <CloudLightning weight="duotone" className={`${className} text-purple-500`} />,
    "snowflake": <Snowflake weight="duotone" className={`${className} text-blue-300`} />,
  };
  return icons[icon] || <Sun weight="duotone" className={`${className} text-amber-500`} />;
};

const EventModal = ({ event, isOpen, onClose, isSaved, onToggleSave }) => {
  const [weather, setWeather] = useState(null);
  const [weatherLoading, setWeatherLoading] = useState(false);

  useEffect(() => {
    if (!event || !isOpen) {
      setWeather(null);
      return;
    }
    setWeatherLoading(true);
    axios.get(`${API}/events/${event.id}/weather`)
      .then(res => setWeather(res.data.weather))
      .catch(() => setWeather(null))
      .finally(() => setWeatherLoading(false));
  }, [event, isOpen]);

  if (!event) return null;

  const getCategoryStyle = (category) => {
    const styles = {
      festival: "badge-festival",
      fair: "badge-fair",
      community: "badge-community"
    };
    return styles[category] || "badge-community";
  };

  const getCategoryLabel = (category) => {
    const labels = {
      festival: "Festival",
      fair: "Fair",
      community: "Community Event"
    };
    return labels[category] || category;
  };

  const formatEventDate = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    if (startDate === endDate) {
      return format(start, "EEEE, MMMM d, yyyy");
    }
    
    return `${format(start, "MMMM d")} - ${format(end, "MMMM d, yyyy")}`;
  };

  const getDuration = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
    return days === 1 ? "1 day event" : `${days} days`;
  };

  const isDeadlinePassed = (deadline) => {
    if (!deadline) return false;
    return new Date(deadline) < new Date();
  };

  const getDaysUntilDeadline = (deadline) => {
    if (!deadline) return null;
    const deadlineDate = new Date(deadline);
    const now = new Date();
    return Math.ceil((deadlineDate - now) / (1000 * 60 * 60 * 24));
  };

  const openInMaps = () => {
    const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.address)}`;
    window.open(url, "_blank");
  };

  const openVendorApp = () => {
    if (event.vendor_app_url) {
      window.open(event.vendor_app_url, "_blank");
    }
  };

  // Calendar export functions
  const formatDateForCalendar = (dateStr) => {
    const date = new Date(dateStr);
    return date.toISOString().replace(/-|:|\.\d{3}/g, '').slice(0, 15) + 'Z';
  };

  const generateGoogleCalendarUrl = (isDeadline = false) => {
    const title = isDeadline 
      ? `DEADLINE: ${event.name} Vendor Application`
      : event.name;
    const startDate = isDeadline ? event.app_deadline : event.start_date;
    const endDate = isDeadline ? event.app_deadline : event.end_date;
    const description = isDeadline
      ? `Vendor application deadline for ${event.name}.\n\nBooth Fee: ${event.booth_fee || 'TBD'}\nBooth Size: ${event.booth_size || 'TBD'}\n\nApply: ${event.vendor_app_url || 'Contact organizer'}`
      : `${event.description}\n\nVendors Wanted: ${event.vendors_wanted || 'Various'}\nBooth Fee: ${event.booth_fee || 'TBD'}\nExpected Attendance: ${event.expected_attendance || 'TBD'}`;
    
    const params = new URLSearchParams({
      action: 'TEMPLATE',
      text: title,
      dates: `${formatDateForCalendar(startDate)}/${formatDateForCalendar(endDate)}`,
      details: description,
      location: event.address,
    });
    
    return `https://calendar.google.com/calendar/render?${params.toString()}`;
  };

  const generateICSFile = (isDeadline = false) => {
    const title = isDeadline 
      ? `DEADLINE: ${event.name} Vendor Application`
      : event.name;
    const startDate = isDeadline ? event.app_deadline : event.start_date;
    const endDate = isDeadline ? event.app_deadline : event.end_date;
    const description = isDeadline
      ? `Vendor application deadline for ${event.name}. Booth Fee: ${event.booth_fee || 'TBD'}. Apply: ${event.vendor_app_url || 'Contact organizer'}`
      : `${event.description} Vendors Wanted: ${event.vendors_wanted || 'Various'}. Booth Fee: ${event.booth_fee || 'TBD'}`;
    
    const icsContent = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//411 Fest//Event Calendar//EN
BEGIN:VEVENT
UID:${event.id}${isDeadline ? '-deadline' : ''}@411fest.com
DTSTAMP:${formatDateForCalendar(new Date().toISOString())}
DTSTART:${formatDateForCalendar(startDate)}
DTEND:${formatDateForCalendar(endDate)}
SUMMARY:${title}
DESCRIPTION:${description.replace(/\n/g, '\\n')}
LOCATION:${event.address}
END:VEVENT
END:VCALENDAR`;

    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${event.name.replace(/\s+/g, '-')}${isDeadline ? '-deadline' : ''}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const daysUntil = getDaysUntilDeadline(event.app_deadline);
  const deadlinePassed = isDeadlinePassed(event.app_deadline);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="max-w-2xl w-[95vw] sm:w-full p-0 overflow-hidden bg-white border-[#D6D3D1] max-h-[90vh] sm:max-h-[85vh] overflow-y-auto"
        data-testid="event-details-modal"
      >
        {/* Hero Image */}
        <div className="relative h-40 sm:h-48 md:h-56 overflow-hidden flex-shrink-0">
          <img
            src={event.image_url}
            alt={event.name}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.src = "https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=800";
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
          
          {/* Category Badge on Image */}
          <div className="absolute top-3 sm:top-4 left-3 sm:left-4">
            <span className={`inline-block px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full text-[10px] sm:text-xs font-semibold uppercase tracking-wider ${getCategoryStyle(event.category)} bg-white/90 backdrop-blur-sm`}>
              {getCategoryLabel(event.category)}
            </span>
          </div>
          
          {/* Close Button & Bookmark */}
          <div className="absolute top-3 sm:top-4 right-3 sm:right-4 flex items-center gap-2">
            {onToggleSave && (
              <button
                onClick={() => onToggleSave(event.id)}
                className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full backdrop-blur-sm flex items-center justify-center transition-colors ${isSaved ? "bg-[#E07A5F] text-white" : "bg-white/90 text-[#2B2D42] hover:bg-white"}`}
                data-testid="modal-bookmark-btn"
                title={isSaved ? "Remove from saved" : "Save this show"}
              >
                <BookmarkSimple weight={isSaved ? "fill" : "bold"} className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
            )}
            <button
              onClick={onClose}
              className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center text-[#2B2D42] hover:bg-white transition-colors"
              data-testid="modal-close-btn"
            >
              <X weight="bold" className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
          </div>
          
          {/* Title on Image */}
          <div className="absolute bottom-3 sm:bottom-4 left-3 sm:left-4 right-3 sm:right-4">
            <DialogHeader>
              <DialogTitle 
                className="text-xl sm:text-2xl md:text-3xl font-bold text-white leading-tight"
                style={{ fontFamily: "'Work Sans', sans-serif" }}
              >
                {event.name}
              </DialogTitle>
              <DialogDescription className="sr-only">
                Vendor application details for {event.name}
              </DialogDescription>
            </DialogHeader>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6">
          {/* Vendor Application CTA - Prominent */}
          {event.vendor_app_url && (
            <div className={`mb-4 sm:mb-6 p-3 sm:p-4 rounded-xl border ${
              deadlinePassed 
                ? "bg-gray-50 border-gray-200" 
                : "bg-[#81B29A]/10 border-[#81B29A]/30"
            }`}>
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Storefront weight="duotone" className={`w-5 h-5 ${deadlinePassed ? "text-gray-400" : "text-[#81B29A]"}`} />
                    <h3 className={`font-semibold text-sm sm:text-base ${deadlinePassed ? "text-gray-500" : "text-[#2B2D42]"}`}>
                      {deadlinePassed ? "Applications Closed" : "Vendor Applications Open"}
                    </h3>
                  </div>
                  {event.app_deadline && (
                    <p className={`text-xs sm:text-sm ${deadlinePassed ? "text-gray-400" : "text-[#6C757D]"}`}>
                      {deadlinePassed 
                        ? `Deadline was ${format(new Date(event.app_deadline), "MMMM d, yyyy")}`
                        : `Apply by ${format(new Date(event.app_deadline), "MMMM d, yyyy")} (${daysUntil} days left)`
                      }
                    </p>
                  )}
                </div>
                <Button
                  onClick={openVendorApp}
                  disabled={deadlinePassed}
                  className={`w-full sm:w-auto ${
                    deadlinePassed 
                      ? "bg-gray-300 text-gray-500 cursor-not-allowed" 
                      : "bg-[#81B29A] hover:bg-[#6A9A83] text-white"
                  } rounded-lg font-semibold px-6`}
                  data-testid="apply-now-btn"
                >
                  <ArrowSquareOut weight="bold" className="w-5 h-5 mr-2" />
                  {deadlinePassed ? "Closed" : "Apply Now"}
                </Button>
              </div>
            </div>
          )}

          {/* Vendor Info Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3 mb-4 sm:mb-6">
            {/* Booth Fee - always shown */}
            <div className="flex items-center gap-2 sm:gap-3 p-2.5 sm:p-3 bg-[#F7F5F0] rounded-lg">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-[#81B29A]/10 flex items-center justify-center flex-shrink-0">
                <CurrencyDollar weight="duotone" className="w-4 h-4 sm:w-5 sm:h-5 text-[#81B29A]" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] sm:text-xs text-[#6C757D] uppercase tracking-wider font-semibold">Booth Fee</p>
                {event.booth_fee ? (
                  <p className="text-xs sm:text-sm text-[#2B2D42] font-medium truncate">{event.booth_fee}</p>
                ) : (
                  <button
                    onClick={() => event.website ? window.open(event.website, "_blank") : openInMaps()}
                    className="text-xs sm:text-sm text-[#E07A5F] font-medium hover:underline flex items-center gap-1"
                    data-testid="booth-fee-contact-link"
                  >
                    <Envelope weight="duotone" className="w-3 h-3" />
                    Contact for Info
                  </button>
                )}
              </div>
            </div>
            
            {/* Booth Size - always shown */}
            <div className="flex items-center gap-2 sm:gap-3 p-2.5 sm:p-3 bg-[#F7F5F0] rounded-lg">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-[#E07A5F]/10 flex items-center justify-center flex-shrink-0">
                <Tag weight="duotone" className="w-4 h-4 sm:w-5 sm:h-5 text-[#E07A5F]" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] sm:text-xs text-[#6C757D] uppercase tracking-wider font-semibold">Booth Size</p>
                {event.booth_size ? (
                  <p className="text-xs sm:text-sm text-[#2B2D42] font-medium truncate">{event.booth_size}</p>
                ) : (
                  <button
                    onClick={() => event.website ? window.open(event.website, "_blank") : openInMaps()}
                    className="text-xs sm:text-sm text-[#E07A5F] font-medium hover:underline flex items-center gap-1"
                    data-testid="booth-size-contact-link"
                  >
                    <Envelope weight="duotone" className="w-3 h-3" />
                    Contact for Info
                  </button>
                )}
              </div>
            </div>
            
            {/* Attendance - always shown */}
            <div className="flex items-center gap-2 sm:gap-3 p-2.5 sm:p-3 bg-[#F7F5F0] rounded-lg">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-[#2B2D42]/10 flex items-center justify-center flex-shrink-0">
                <Users weight="duotone" className="w-4 h-4 sm:w-5 sm:h-5 text-[#2B2D42]" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] sm:text-xs text-[#6C757D] uppercase tracking-wider font-semibold">Attendance</p>
                {event.expected_attendance ? (
                  <p className="text-xs sm:text-sm text-[#2B2D42] font-medium truncate">{event.expected_attendance}</p>
                ) : (
                  <button
                    onClick={() => event.website ? window.open(event.website, "_blank") : openInMaps()}
                    className="text-xs sm:text-sm text-[#E07A5F] font-medium hover:underline flex items-center gap-1"
                    data-testid="attendance-contact-link"
                  >
                    <Envelope weight="duotone" className="w-3 h-3" />
                    Contact for Info
                  </button>
                )}
              </div>
            </div>
            
            <div className="flex items-center gap-2 sm:gap-3 p-2.5 sm:p-3 bg-[#F7F5F0] rounded-lg">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-[#E07A5F]/10 flex items-center justify-center flex-shrink-0">
                <CalendarBlank weight="duotone" className="w-4 h-4 sm:w-5 sm:h-5 text-[#E07A5F]" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] sm:text-xs text-[#6C757D] uppercase tracking-wider font-semibold">Duration</p>
                <p className="text-xs sm:text-sm text-[#2B2D42] font-medium truncate">{getDuration(event.start_date, event.end_date)}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2 sm:gap-3 p-2.5 sm:p-3 bg-[#F7F5F0] rounded-lg">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-[#81B29A]/10 flex items-center justify-center flex-shrink-0">
                <Ticket weight="duotone" className="w-4 h-4 sm:w-5 sm:h-5 text-[#81B29A]" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] sm:text-xs text-[#6C757D] uppercase tracking-wider font-semibold">Entry</p>
                <p className="text-xs sm:text-sm text-[#2B2D42] font-medium truncate">{event.price}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2 sm:gap-3 p-2.5 sm:p-3 bg-[#F7F5F0] rounded-lg">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-[#E07A5F]/10 flex items-center justify-center flex-shrink-0">
                <Clock weight="duotone" className="w-4 h-4 sm:w-5 sm:h-5 text-[#E07A5F]" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] sm:text-xs text-[#6C757D] uppercase tracking-wider font-semibold">Hours</p>
                <p className="text-xs sm:text-sm text-[#2B2D42] font-medium truncate" data-testid="event-time">
                  {event.time || "See Website"}
                </p>
              </div>
            </div>
          </div>

          {/* Date Range Bar */}
          <div className="mb-4 sm:mb-6 p-3 bg-[#F7F5F0] rounded-lg flex items-center gap-3">
            <CalendarBlank weight="duotone" className="w-5 h-5 text-[#E07A5F] flex-shrink-0" />
            <div>
              <p className="text-sm text-[#2B2D42] font-medium" data-testid="event-date-range">
                {format(new Date(event.start_date), "EEEE, MMMM d, yyyy")}
                {event.end_date && event.end_date !== event.start_date && (
                  <> &mdash; {format(new Date(event.end_date), "EEEE, MMMM d, yyyy")}</>
                )}
              </p>
              {event.time && (
                <p className="text-xs text-[#6C757D] mt-0.5">Daily hours: {event.time}</p>
              )}
            </div>
          </div>

          {/* Vendors Wanted */}
          {event.vendors_wanted && (
            <div className="mb-4 sm:mb-6">
              <h3 className="text-xs text-[#6C757D] uppercase tracking-wider font-semibold mb-2">Vendors Wanted</h3>
              <div className="flex flex-wrap gap-1.5 sm:gap-2">
                {event.vendors_wanted.split(", ").map((vendor) => (
                  <span 
                    key={vendor}
                    className="px-2.5 sm:px-3 py-0.5 sm:py-1 bg-[#E07A5F]/10 text-[#E07A5F] rounded-full text-xs sm:text-sm font-medium"
                  >
                    {vendor}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Location */}
          <div className="mb-4 sm:mb-6">
            <h3 className="text-xs text-[#6C757D] uppercase tracking-wider font-semibold mb-2">Location</h3>
            <div className="flex items-start gap-3 p-3 sm:p-4 bg-[#F7F5F0] rounded-lg">
              <MapPin weight="duotone" className="w-5 h-5 text-[#81B29A] flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm sm:text-base text-[#2B2D42] font-medium">{event.location}</p>
                <p className="text-xs sm:text-sm text-[#6C757D]">{event.address}</p>
              </div>
            </div>
          </div>

          {/* Weather */}
          {weather && (
            <div className="mb-4 sm:mb-6" data-testid="event-weather-section">
              <h3 className="text-xs text-[#6C757D] uppercase tracking-wider font-semibold mb-2">
                {weather.source === "forecast" ? "Weather Forecast" : "Typical Weather"}
              </h3>
              <div className="flex items-center gap-4 p-3 sm:p-4 bg-gradient-to-r from-blue-50 to-amber-50 rounded-lg border border-blue-100/50">
                <WeatherIcon icon={weather.icon} className="w-10 h-10" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-[#2B2D42]">{weather.condition}</p>
                  <div className="flex items-center gap-3 mt-0.5">
                    <span className="text-sm text-[#E07A5F] font-medium" data-testid="weather-high">
                      <Thermometer weight="bold" className="w-3.5 h-3.5 inline mr-0.5" />
                      {weather.high_f}°F
                    </span>
                    {weather.low_f && (
                      <span className="text-sm text-[#81B29A]" data-testid="weather-low">
                        Low {weather.low_f}°F
                      </span>
                    )}
                    {weather.precipitation_chance != null && (
                      <span className="text-xs text-blue-500" data-testid="weather-precip">
                        <CloudRain weight="bold" className="w-3 h-3 inline mr-0.5" />
                        {weather.precipitation_chance}% rain
                      </span>
                    )}
                  </div>
                </div>
                <span className="text-[9px] text-[#A8A5A1] uppercase self-start">{weather.label}</span>
              </div>
            </div>
          )}
          {weatherLoading && (
            <div className="mb-4 sm:mb-6">
              <div className="flex items-center gap-2 p-3 bg-[#F7F5F0] rounded-lg">
                <div className="w-4 h-4 border-2 border-[#E07A5F] border-t-transparent rounded-full animate-spin" />
                <span className="text-xs text-[#6C757D]">Loading weather...</span>
              </div>
            </div>
          )}

          {/* Description */}
          <div className="mb-4 sm:mb-6">
            <h3 className="text-xs text-[#6C757D] uppercase tracking-wider font-semibold mb-2">About This Show</h3>
            <p className="text-sm sm:text-base text-[#2B2D42]/90 leading-relaxed">
              {event.description}
            </p>
          </div>

          {/* Contact Information */}
          {(event.contact_name || event.contact_email || event.contact_phone || event.organizer) && (
            <div className="mb-4 sm:mb-6" data-testid="event-contact-section">
              <h3 className="text-xs text-[#6C757D] uppercase tracking-wider font-semibold mb-2">Contact Information</h3>
              <div className="bg-[#F7F5F0] rounded-lg p-3 sm:p-4 space-y-2.5">
                {event.organizer && (
                  <div className="flex items-center gap-3">
                    <Storefront weight="duotone" className="w-4 h-4 text-[#81B29A] flex-shrink-0" />
                    <span className="text-sm text-[#2B2D42]" data-testid="event-organizer">{event.organizer}</span>
                  </div>
                )}
                {event.contact_name && (
                  <div className="flex items-center gap-3">
                    <Users weight="duotone" className="w-4 h-4 text-[#3D405B] flex-shrink-0" />
                    <span className="text-sm text-[#2B2D42]" data-testid="event-contact-name">{event.contact_name}</span>
                  </div>
                )}
                {event.contact_email && (
                  <div className="flex items-center gap-3">
                    <Envelope weight="duotone" className="w-4 h-4 text-[#E07A5F] flex-shrink-0" />
                    <a
                      href={`mailto:${event.contact_email}`}
                      className="text-sm text-[#E07A5F] hover:underline"
                      data-testid="event-contact-email"
                    >
                      {event.contact_email}
                    </a>
                  </div>
                )}
                {event.contact_phone && (
                  <div className="flex items-center gap-3">
                    <Phone weight="duotone" className="w-4 h-4 text-[#81B29A] flex-shrink-0" />
                    <a
                      href={`tel:${event.contact_phone.replace(/[^\d+]/g, '')}`}
                      className="text-sm text-[#81B29A] hover:underline"
                      data-testid="event-contact-phone"
                    >
                      {event.contact_phone}
                    </a>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
            {event.vendor_app_url && !deadlinePassed && (
              <Button
                onClick={openVendorApp}
                className="flex-1 h-11 sm:h-12 bg-[#E07A5F] hover:bg-[#D46A50] text-white rounded-lg font-medium transition-colors text-sm sm:text-base"
                data-testid="apply-vendor-btn"
              >
                <Storefront weight="bold" className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                Apply as Vendor
              </Button>
            )}
            
            {/* Calendar Export Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className="flex-1 h-11 sm:h-12 border-[#D6D3D1] text-[#2B2D42] hover:bg-[#F7F5F0] rounded-lg font-medium text-sm sm:text-base"
                  data-testid="add-to-calendar-btn"
                >
                  <CalendarPlus weight="bold" className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                  Add to Calendar
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem 
                  onClick={() => window.open(generateGoogleCalendarUrl(false), '_blank')}
                  className="cursor-pointer"
                >
                  <CalendarBlank weight="duotone" className="w-4 h-4 mr-2 text-[#E07A5F]" />
                  Event to Google Calendar
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => generateICSFile(false)}
                  className="cursor-pointer"
                >
                  <CalendarBlank weight="duotone" className="w-4 h-4 mr-2 text-[#81B29A]" />
                  Event to Apple/Outlook (.ics)
                </DropdownMenuItem>
                {event.app_deadline && !deadlinePassed && (
                  <>
                    <DropdownMenuItem 
                      onClick={() => window.open(generateGoogleCalendarUrl(true), '_blank')}
                      className="cursor-pointer"
                    >
                      <Clock weight="duotone" className="w-4 h-4 mr-2 text-[#E07A5F]" />
                      Deadline to Google Calendar
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => generateICSFile(true)}
                      className="cursor-pointer"
                    >
                      <Clock weight="duotone" className="w-4 h-4 mr-2 text-[#81B29A]" />
                      Deadline to Apple/Outlook (.ics)
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            
            <Button
              onClick={openInMaps}
              variant="outline"
              className="flex-1 h-11 sm:h-12 border-[#D6D3D1] text-[#2B2D42] hover:bg-[#F7F5F0] rounded-lg font-medium text-sm sm:text-base"
              data-testid="get-directions-btn"
            >
              <NavigationArrow weight="bold" className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
              Directions
            </Button>
            
            {event.website && (
              <Button
                onClick={() => window.open(event.website, "_blank")}
                variant="outline"
                className="h-11 sm:h-12 border-[#D6D3D1] text-[#2B2D42] hover:bg-[#F7F5F0] rounded-lg font-medium px-4"
                data-testid="visit-website-btn"
              >
                <Globe weight="duotone" className="w-4 h-4 sm:w-5 sm:h-5" />
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EventModal;
